import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChatButton } from '@/components/AIChat';
import { ExplanationRenderer } from '@/components/QuestionRenderer';
import ContentProtection from '@/components/ContentProtection';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  XCircle,
  GraduationCap,
  Loader2,
  Home,
  Shuffle,
  Languages
} from 'lucide-react';

// Default track info for known certification tracks
const DEFAULT_TRACK_INFO = {
  PMP: { name: 'PMP', fullName: 'Project Management Professional' },
  CAPM: { name: 'CAPM', fullName: 'Certified Associate in Project Management' },
  PSM: { name: 'PSM', fullName: 'Professional Scrum Master' },
  PSPO: { name: 'PSPO', fullName: 'Professional Scrum Product Owner' },
  PRINCE2: { name: 'PRINCE2', fullName: 'PRINCE2 Foundation' },
  SAFe: { name: 'SAFe', fullName: 'SAFe Foundation' },
  SAFE: { name: 'SAFe', fullName: 'SAFe Foundation' },
  ITIL4: { name: 'ITIL4', fullName: 'ITIL 4 Foundation' }
};

const getTrackDisplayName = (trackCode) => {
  return DEFAULT_TRACK_INFO[trackCode]?.fullName || trackCode;
};

const TrainingMode = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getAuthHeaders, user, refreshUser } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  const [step, setStep] = useState('setup'); // setup, quiz, complete
  const [questionCount, setQuestionCount] = useState('10');
  const [loading, setLoading] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState(user?.selected_track || 'PMP');
  
  const [attemptId, setAttemptId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [matchingAnswers, setMatchingAnswers] = useState({}); // For matching questions
  const [shuffledRightItems, setShuffledRightItems] = useState({}); // Stable shuffled options for matching
  const [feedback, setFeedback] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [showEnglish, setShowEnglish] = useState(false); // Toggle to show English original
  const [timeLimit, setTimeLimit] = useState(0);
  const [isCustomSession, setIsCustomSession] = useState(false);

  // Get user's preferred question language
  const userLanguage = user?.question_language || 'en';
  
  // Language display info
  const languageInfo = {
    en: { flag: '🇬🇧', name: 'English' },
    de: { flag: '🇩🇪', name: 'German' },
    es: { flag: '🇪🇸', name: 'Spanish' },
    fr: { flag: '🇫🇷', name: 'French' },
    zh: { flag: '🇨🇳', name: 'Chinese' },
    ru: { flag: '🇷🇺', name: 'Russian' },
    it: { flag: '🇮🇹', name: 'Italian' },
    pl: { flag: '🇵🇱', name: 'Polish' },
    sv: { flag: '🇸🇪', name: 'Swedish' },
    pt: { flag: '🇵🇹', name: 'Portuguese' },
    nl: { flag: '🇳🇱', name: 'Dutch' },
    tr: { flag: '🇹🇷', name: 'Turkish' },
    ar: { flag: '🇸🇦', name: 'Arabic' },
    hi: { flag: '🇮🇳', name: 'Hindi' }
  };

  // Helper to get translated text based on current view mode
  const getQuestionText = (question) => {
    if (!question) return '';
    // If showing English or user language is English, show original
    if (showEnglish || userLanguage === 'en') {
      return question.question_text || '';
    }
    // Try to get translated text, fallback to English
    const translatedField = `question_text_${userLanguage}`;
    return question[translatedField] || question.question_text || '';
  };

  const getChoiceText = (choice) => {
    if (!choice) return '';
    if (showEnglish || userLanguage === 'en') {
      return choice.text || '';
    }
    const translatedField = `text_${userLanguage}`;
    return choice[translatedField] || choice.text || '';
  };

  const getExplanationText = (question) => {
    if (!question) return '';
    if (showEnglish || userLanguage === 'en') {
      return question.explanation || '';
    }
    const translatedField = `explanation_${userLanguage}`;
    return question[translatedField] || question.explanation || '';
  };

  // Check if translation is available for the user's language
  const hasTranslation = (question) => {
    if (!question || userLanguage === 'en') return false;
    const translatedField = `question_text_${userLanguage}`;
    return !!question[translatedField];
  };

  // State for available courses (for admin)
  const [allCourses, setAllCourses] = useState([]);

  // Fetch available courses for admins
  useEffect(() => {
    const fetchCourses = async () => {
      if (isAdmin) {
        try {
          const response = await fetch(`${API}/categories`);
          if (response.ok) {
            const data = await response.json();
            // Extract course codes from the response - handle both formats
            const categoriesArray = data.categories || data;
            const courses = categoriesArray.map(c => c.id || c.code || c.name).filter(Boolean);
            if (courses.length > 0) {
              setAllCourses(courses);
            }
          }
        } catch (error) {
          console.error('Failed to fetch courses:', error);
        }
      }
    };
    fetchCourses();
  }, [isAdmin]);

  // Get available tracks for user
  const getAvailableTracks = () => {
    if (user?.is_guest) {
      // Guests only get access to PMP (or their selected track)
      return [user?.selected_track || 'PMP'];
    }
    
    // Admins get access to all courses
    if (isAdmin && allCourses.length > 0) {
      return allCourses;
    }
    
    // For registered users, get tracks from track_access
    const userTrackAccess = user?.track_access || [];
    const activeTracks = userTrackAccess.filter(t => t.is_active).map(t => t.track);
    
    // If no tracks found, fallback to selected_track or PMP
    if (activeTracks.length === 0) {
      return [user?.selected_track || 'PMP'];
    }
    
    return activeTracks;
  };

  const availableTracks = getAvailableTracks();

  // Update selected track when user changes
  useEffect(() => {
    if (user?.selected_track && !selectedTrack) {
      setSelectedTrack(user.selected_track);
    }
  }, [user?.selected_track, selectedTrack]);

  // Check for custom session data on mount
  useEffect(() => {
    // Check if coming from Custom Session
    const customSessionData = sessionStorage.getItem('customSession');
    if (customSessionData || location.state?.customSession) {
      try {
        const sessionData = customSessionData ? JSON.parse(customSessionData) : null;
        
        if (sessionData && sessionData.questions && sessionData.attemptId) {
          // Load custom session directly - skip setup
          setAttemptId(sessionData.attemptId);
          setQuestions(sessionData.questions);
          setTimeLimit(sessionData.timeLimit || 0);
          setIsCustomSession(true);
          // Set selected track from custom session
          if (sessionData.track) {
            setSelectedTrack(sessionData.track);
          }
          setStep('quiz');
          
          // Clear session storage to prevent reload issues
          sessionStorage.removeItem('customSession');
        }
      } catch (e) {
        console.error('Failed to load custom session:', e);
        sessionStorage.removeItem('customSession');
      }
    }
  }, [location.state]);

  const toggleEnglishView = () => {
    setShowEnglish(!showEnglish);
  };

  // Reset translation toggle when moving to next question
  const resetTranslation = () => {
    setShowEnglish(false);
  };

  const startTraining = async () => {
    setLoading(true);
    try {
      // Guests always get 50 questions, registered users use their selection
      const questionsToRequest = user?.is_guest ? 50 : parseInt(questionCount);
      
      const response = await fetch(`${API}/exams/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          exam_type: selectedTrack,
          mode: 'training',
          question_count: questionsToRequest,
          category: selectedTrack
        })
      });

      // Read body only once
      let data;
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        data = {};
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to start training');
      }

      setAttemptId(data.attempt_id);
      setQuestions(data.questions);
      setStep('quiz');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const currentQuestion = questions[currentIndex];
  
  // Check if translation toggle should be available (user not using English)
  const showTranslateButton = userLanguage !== 'en';

  const handleAnswer = (value) => {
    if (feedback) return; // Already answered
    
    const questionId = currentQuestion.question_id;
    let newAnswer;
    
    if (currentQuestion.question_type === 'multi') {
      const current = answers[questionId] || [];
      if (current.includes(value)) {
        newAnswer = current.filter(v => v !== value);
      } else {
        newAnswer = [...current, value];
      }
    } else if (currentQuestion.question_type === 'fill') {
      newAnswer = [value];
    } else {
      newAnswer = [value];
    }
    
    setAnswers(prev => ({ ...prev, [questionId]: newAnswer }));
  };

  // Handle matching question answer
  const handleMatchingAnswer = (leftItem, rightItem) => {
    if (feedback) return;
    
    const questionId = currentQuestion.question_id;
    setMatchingAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...(prev[questionId] || {}),
        [leftItem]: rightItem
      }
    }));
  };

  const submitAnswer = async () => {
    const questionId = currentQuestion.question_id;
    let userAnswer = [];
    
    if (currentQuestion.question_type === 'match') {
      // Convert matching answers to format expected by backend
      const matches = matchingAnswers[questionId] || {};
      userAnswer = Object.entries(matches).map(([left, right]) => `${left}:${right}`);
    } else {
      userAnswer = answers[questionId] || [];
    }
    
    if (userAnswer.length === 0) {
      toast.error('Please select an answer');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/exams/${attemptId}/answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          question_id: questionId,
          selected_answers: userAnswer
        })
      });

      // Read body only once
      let data;
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        data = {};
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit answer');
      }

      setFeedback(data);
      setShowExplanation(true);
    } catch (error) {
      toast.error(error.message || 'Failed to submit answer');
    } finally {
      setLoading(false);
    }
  };

  const nextQuestion = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setFeedback(null);
      setShowExplanation(false);
      resetTranslation();
    } else {
      completeTraining();
    }
  };

  const completeTraining = async () => {
    try {
      const response = await fetch(`${API}/exams/${attemptId}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({ time_used: null })
      });

      if (response.ok) {
        navigate(`/results/${attemptId}`);
      }
    } catch (error) {
      toast.error('Failed to complete training');
    }
  };

  // Guest Limit Reached Screen
  const guestLimitReached = user?.is_guest && (user?.questions_answered || 0) >= (user?.max_questions || 20);
  
  if (guestLimitReached && step === 'setup') {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4">
        <div className="max-w-xl mx-auto pt-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>

          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0047AB] to-[#003380] flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight mb-2">Great Start!</h1>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                You've completed your {user?.max_questions || 20} free trial questions for {user?.selected_track}.
              </p>
              
              <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-xl mb-6">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>Want to continue learning?</strong><br />
                  Register for free to access unlimited questions, track your progress, and prepare effectively for your certification.
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <Button 
                  className="w-full bg-[#0047AB] hover:bg-[#003380] text-white py-6 text-lg"
                  onClick={() => navigate('/register')}
                  data-testid="register-btn"
                >
                  Register for Free
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/login')}
                >
                  Already have an account? Login
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Setup Screen
  if (step === 'setup') {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4">
        <div className="max-w-xl mx-auto pt-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="mb-6"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0047AB] to-[#003380] flex items-center justify-center mx-auto mb-4">
                  <GraduationCap className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-2xl font-bold tracking-tight">Training Mode</h1>
                <p className="text-slate-600 dark:text-slate-400 mt-2">
                  Practice with instant feedback
                </p>
              </div>

              <div className="space-y-6">
                {/* Track Selection */}
                <div className="space-y-2">
                  <Label>Select Course</Label>
                  {availableTracks.length > 1 ? (
                    <Select value={selectedTrack} onValueChange={setSelectedTrack}>
                      <SelectTrigger data-testid="track-select">
                        <SelectValue placeholder="Choose a course" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableTracks.map(track => (
                          <SelectItem key={track} value={track}>
                            {getTrackDisplayName(track)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="p-4 bg-[#0047AB]/5 dark:bg-[#0047AB]/10 border border-[#0047AB]/20 rounded-xl">
                      <p className="font-semibold text-lg text-[#0047AB]">
                        {getTrackDisplayName(selectedTrack)}
                      </p>
                      {!user?.is_guest && (
                        <p className="text-xs text-slate-500 mt-1">
                          Purchase more courses in the Store to unlock them
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Show dropdown only for registered users, fixed 50 for guests */}
                {user?.is_guest ? (
                  <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-xl">
                    <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">
                      Free trial: 50 questions
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      Register for unlimited access
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Number of Questions</Label>
                    <Select value={questionCount} onValueChange={setQuestionCount}>
                      <SelectTrigger data-testid="question-count-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10 questions</SelectItem>
                        <SelectItem value="20">20 questions</SelectItem>
                        <SelectItem value="50">50 questions</SelectItem>
                        <SelectItem value="100">100 questions</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Button 
                  className="w-full h-12 bg-[#0047AB] hover:bg-[#003380] text-white rounded-xl"
                  onClick={startTraining}
                  disabled={loading || !selectedTrack}
                  data-testid="start-training-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Start Training'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Quiz Screen
  if (!currentQuestion) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Loader2 className="w-8 h-8 animate-spin text-[#0047AB]" />
      </div>
    );
  }

  const progress = ((currentIndex + 1) / questions.length) * 100;

  // Get or create shuffled right items for matching questions
  const getShuffledRightItems = (questionId, matchPairs) => {
    if (!shuffledRightItems[questionId]) {
      const rightItems = matchPairs.map(p => p.right);
      // Fisher-Yates shuffle
      const shuffled = [...rightItems];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      setShuffledRightItems(prev => ({ ...prev, [questionId]: shuffled }));
      return shuffled;
    }
    return shuffledRightItems[questionId];
  };

  // Render matching question
  const renderMatchingQuestion = () => {
    const matchPairs = currentQuestion.match_pairs || [];
    if (matchPairs.length === 0) {
      return (
        <div className="p-4 bg-yellow-50 dark:bg-yellow-950/20 rounded-xl text-yellow-700 dark:text-yellow-400">
          This question has no matching pairs.
        </div>
      );
    }
    
    // For matching questions, always use English for now (translations not yet implemented for match pairs)
    const leftItems = matchPairs.map(p => p.left);
    const rightItemsShuffled = getShuffledRightItems(currentQuestion.question_id, matchPairs);
    const rightItems = rightItemsShuffled.map(right => {
      const pair = matchPairs.find(p => p.right === right);
      return pair?.right || right;
    });
    const currentMatches = matchingAnswers[currentQuestion.question_id] || {};
    const selectedLeftIndex = Object.keys(currentMatches).length;

    return (
      <div className="space-y-4" data-testid="matching-question">
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
          Match each term on the left with the correct definition on the right. Click a term on the left, then click the matching definition on the right.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left column - Terms */}
          <div className="space-y-2">
            <p className="font-medium text-sm mb-2 text-[#0047AB]">Terms</p>
            {leftItems.map((left, idx) => {
              const originalLeft = matchPairs[idx].left;
              const isMatched = currentMatches[originalLeft];
              const isNext = !isMatched && idx === selectedLeftIndex;
              
              return (
                <div
                  key={idx}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    isMatched 
                      ? 'border-green-500 bg-green-50 dark:bg-green-950/20' 
                      : isNext
                        ? 'border-[#0047AB] bg-[#0047AB]/5 dark:bg-[#0047AB]/10 ring-2 ring-[#0047AB]/30'
                        : 'border-slate-200 dark:border-slate-700'
                  }`}
                  data-testid={`match-left-${idx}`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      isMatched ? 'bg-green-500 text-white' : isNext ? 'bg-[#0047AB] text-white' : 'bg-slate-200 dark:bg-slate-700'
                    }`}>
                      {idx + 1}
                    </span>
                    <span>{left}</span>
                  </div>
                  {isMatched && (
                    <div className="mt-2 text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      → {currentMatches[originalLeft]}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Right column - Definitions */}
          <div className="space-y-2">
            <p className="font-medium text-sm mb-2 text-[#0047AB]">Definitions</p>
            {rightItems.map((right, idx) => {
              const originalRight = rightItemsShuffled[idx];
              const isUsed = Object.values(currentMatches).includes(originalRight);
              
              return (
                <div
                  key={idx}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    isUsed
                      ? 'border-green-500 bg-green-50 dark:bg-green-950/20 opacity-60'
                      : feedback
                        ? 'border-slate-200 dark:border-slate-700'
                        : 'border-slate-200 dark:border-slate-700 hover:border-[#0047AB] hover:bg-[#0047AB]/5 dark:hover:bg-[#0047AB]/10 cursor-pointer'
                  }`}
                  onClick={() => {
                    if (!isUsed && !feedback && selectedLeftIndex < leftItems.length) {
                      const originalLeft = matchPairs[selectedLeftIndex].left;
                      handleMatchingAnswer(originalLeft, originalRight);
                    }
                  }}
                  data-testid={`match-right-${idx}`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      isUsed ? 'bg-green-500 text-white' : 'bg-slate-200 dark:bg-slate-700'
                    }`}>
                      {String.fromCharCode(65 + idx)}
                    </span>
                    <span>{right}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Reset button */}
        {Object.keys(currentMatches).length > 0 && !feedback && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMatchingAnswers(prev => ({ ...prev, [currentQuestion.question_id]: {} }))}
            className="mt-4"
            data-testid="reset-matches-btn"
          >
            <Shuffle className="w-4 h-4 mr-2" />
            Reset matches
          </Button>
        )}
        
        {/* Progress indicator */}
        {!feedback && (
          <div className="text-sm text-slate-500 dark:text-slate-400 mt-2">
            {Object.keys(currentMatches).length} of {leftItems.length} matched
          </div>
        )}
      </div>
    );
  };

  return (
    <ContentProtection isAdmin={isAdmin} userEmail={user?.email} userName={user?.name}>
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pb-20 md:pb-0">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="exit-btn">
              <Home className="w-4 h-4 mr-2" />
              Exit
            </Button>
            <span className="text-sm font-medium">
              Question {currentIndex + 1} of {questions.length}
            </span>
            <div className="w-16" /> {/* Spacer for alignment */}
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <Card className="quiz-card question-enter" data-testid="question-card">
          <CardContent className="p-0">
            {/* Category Badge + Language Toggle Button */}
            <div className="flex items-center justify-between mb-4">
              <span className={`category-badge ${(currentQuestion.category || 'pmp').toLowerCase()}`}>
                {currentQuestion.category || 'PMP'}
              </span>
              {showTranslateButton && (
                <Button
                  variant={showEnglish ? "default" : "outline"}
                  size="sm"
                  onClick={toggleEnglishView}
                  className={`gap-2 ${showEnglish ? 'bg-[#0047AB] hover:bg-[#003380]' : ''}`}
                  data-testid="translate-btn"
                >
                  <Languages className="w-4 h-4" />
                  {showEnglish 
                    ? '🇬🇧 English' 
                    : `${languageInfo[userLanguage]?.flag || '🌐'} ${languageInfo[userLanguage]?.name || userLanguage}`
                  }
                </Button>
              )}
            </div>

            {/* Scenario (if present) - display before question text */}
            {currentQuestion.scenario && (
              <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-xl border-l-4 border-blue-500" data-testid="scenario-box">
                <p className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">Szenario:</p>
                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                  {currentQuestion.scenario}
                </p>
              </div>
            )}

            {/* Question Text - in user's language or English */}
            <h2 className="text-xl font-semibold mb-2 leading-relaxed" data-testid="question-text">
              {getQuestionText(currentQuestion)}
            </h2>
            
            {/* Show original English if user is viewing translated version and toggle is off */}
            {!showEnglish && userLanguage !== 'en' && hasTranslation(currentQuestion) && (
              <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-xl border border-blue-200 dark:border-blue-900">
                <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 mb-2">
                  <Languages className="w-4 h-4" />
                  🇬🇧 Original (English)
                </div>
                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                  {currentQuestion.question_text}
                </p>
              </div>
            )}
            
            {(showEnglish || userLanguage === 'en' || !hasTranslation(currentQuestion)) && <div className="mb-6" />}

            {/* Answer Options */}
            <div className="space-y-3">
              {/* Handle missing choices */}
              {(!currentQuestion.choices || currentQuestion.choices.length === 0) && 
               currentQuestion.question_type !== 'match' && 
               currentQuestion.question_type !== 'matching' && (
                <div className="p-4 bg-yellow-50 dark:bg-yellow-950/20 rounded-xl border border-yellow-200 dark:border-yellow-800">
                  <p className="text-yellow-700 dark:text-yellow-400 font-medium">
                    This question has no answer choices available.
                  </p>
                  <p className="text-sm text-yellow-600 dark:text-yellow-500 mt-1">
                    Please skip to the next question.
                  </p>
                </div>
              )}

              {currentQuestion.question_type === 'single' && currentQuestion.choices && currentQuestion.choices.length > 0 && (
                <>
                  <div className="bg-slate-100 dark:bg-slate-800 rounded-lg px-4 py-2 mb-3">
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Select 1 answer
                    </p>
                  </div>
                  <RadioGroup
                    value={answers[currentQuestion.question_id]?.[0] || ''}
                    onValueChange={(value) => handleAnswer(value)}
                    disabled={!!feedback}
                  >
                    {currentQuestion.choices.map((choice, idx) => {
                      const isSelected = answers[currentQuestion.question_id]?.includes(choice.id);
                      const isCorrect = feedback?.correct_answers?.includes(choice.id);
                      const isWrong = feedback && isSelected && !isCorrect;
                      
                      return (
                        <div
                          key={choice.id}
                          className={`choice-option ${isSelected ? 'selected' : ''} ${isCorrect && feedback ? 'correct' : ''} ${isWrong ? 'incorrect' : ''} ${feedback ? 'disabled' : ''}`}
                          data-testid={`choice-${choice.id}`}
                        >
                          <RadioGroupItem value={choice.id} id={choice.id} />
                          <Label htmlFor={choice.id} className="flex-1 cursor-pointer">
                            <span>{getChoiceText(choice)}</span>
                          {/* Show English original for choices when in translated mode */}
                          {!showEnglish && userLanguage !== 'en' && choice[`text_${userLanguage}`] && (
                            <span className="block text-sm text-blue-600 dark:text-blue-400 mt-1">
                              🇬🇧 {choice.text}
                            </span>
                          )}
                        </Label>
                        {feedback && isCorrect && <CheckCircle className="w-5 h-5 text-green-500" />}
                        {isWrong && <XCircle className="w-5 h-5 text-red-500" />}
                      </div>
                    );
                  })}
                  </RadioGroup>
                </>
              )}

              {(currentQuestion.question_type === 'multi' || currentQuestion.question_type === 'multiple') && currentQuestion.choices && currentQuestion.choices.length > 0 && (
                <div className="space-y-3">
                  <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-2 mb-3">
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      {currentQuestion.correct_answers?.length === 1 
                        ? "Select 1 answer" 
                        : `Select ${currentQuestion.correct_answers?.length || 'all that apply'} answers`}
                    </p>
                  </div>
                  {currentQuestion.choices.map((choice, idx) => {
                    const isSelected = answers[currentQuestion.question_id]?.includes(choice.id);
                    const isCorrect = feedback?.correct_answers?.includes(choice.id);
                    const isWrong = feedback && isSelected && !isCorrect;
                    
                    return (
                      <div
                        key={choice.id}
                        className={`choice-option ${isSelected ? 'selected' : ''} ${isCorrect && feedback ? 'correct' : ''} ${isWrong ? 'incorrect' : ''} ${feedback ? 'disabled' : ''}`}
                        onClick={() => !feedback && handleAnswer(choice.id)}
                        data-testid={`choice-${choice.id}`}
                      >
                        <Checkbox 
                          checked={isSelected || false}
                          disabled={!!feedback}
                        />
                        <div className="flex-1">
                          <span>{getChoiceText(choice)}</span>
                          {/* Show English original for choices when in translated mode */}
                          {!showEnglish && userLanguage !== 'en' && choice[`text_${userLanguage}`] && (
                            <span className="block text-sm text-blue-600 dark:text-blue-400 mt-1">
                              🇬🇧 {choice.text}
                            </span>
                          )}
                        </div>
                        {feedback && isCorrect && <CheckCircle className="w-5 h-5 text-green-500" />}
                        {isWrong && <XCircle className="w-5 h-5 text-red-500" />}
                      </div>
                    );
                  })}
                </div>
              )}

              {currentQuestion.question_type === 'fill' && (
                <div className="space-y-2">
                  <Input
                    placeholder="Type your answer..."
                    value={answers[currentQuestion.question_id]?.[0] || ''}
                    onChange={(e) => handleAnswer(e.target.value)}
                    disabled={!!feedback}
                    className="h-12"
                    data-testid="fill-input"
                  />
                  {feedback && (
                    <p className={`text-sm ${feedback.is_correct ? 'text-green-600' : 'text-red-600'}`}>
                      Correct answer: {feedback.correct_answers.join(' or ')}
                    </p>
                  )}
                </div>
              )}

              {currentQuestion.question_type === 'truefalse' && (
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { value: 'true', label: 'True', icon: CheckCircle, color: 'green' },
                    { value: 'false', label: 'False', icon: XCircle, color: 'red' }
                  ].map(({ value, label, icon: Icon, color }) => {
                    const isSelected = answers[currentQuestion.question_id]?.[0] === value;
                    const isCorrect = feedback?.correct_answers?.includes(value);
                    const isWrong = feedback && isSelected && !isCorrect;
                    
                    return (
                      <button
                        key={value}
                        onClick={() => !feedback && handleAnswer(value)}
                        disabled={!!feedback}
                        className={`p-6 rounded-xl border-2 flex flex-col items-center gap-3 transition-all ${
                          feedback
                            ? isCorrect
                              ? 'border-green-500 bg-green-50 dark:bg-green-950/30'
                              : isWrong
                                ? 'border-red-500 bg-red-50 dark:bg-red-950/30'
                                : 'border-slate-200 dark:border-slate-700 opacity-50'
                            : isSelected
                              ? `border-${color}-500 bg-${color}-50 dark:bg-${color}-950/30`
                              : `border-slate-200 dark:border-slate-700 hover:border-${color}-500/50`
                        }`}
                        data-testid={`truefalse-${value}`}
                      >
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          isCorrect
                            ? 'bg-green-500 text-white'
                            : isWrong
                              ? 'bg-red-500 text-white'
                              : isSelected
                                ? `bg-${color}-500 text-white`
                                : `bg-${color}-100 dark:bg-${color}-900/30 text-${color}-600`
                        }`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <span className="font-semibold text-lg">{label}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {currentQuestion.question_type === 'match' && renderMatchingQuestion()}
            </div>

            {/* Explanation */}
            {showExplanation && feedback && (
              <div className="mt-6">
                <ExplanationRenderer 
                  question={currentQuestion} 
                  isCorrect={feedback.is_correct}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
            disabled={currentIndex === 0}
            data-testid="prev-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>

          {!feedback ? (
            <Button
              className="bg-[#0047AB] hover:bg-[#003380] text-white"
              onClick={submitAnswer}
              disabled={loading || (!answers[currentQuestion.question_id]?.length && !Object.keys(matchingAnswers[currentQuestion.question_id] || {}).length)}
              data-testid="submit-answer-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Check Answer'}
            </Button>
          ) : (
            <Button
              className="bg-[#0047AB] hover:bg-[#003380] text-white"
              onClick={nextQuestion}
              data-testid="next-btn"
            >
              {currentIndex < questions.length - 1 ? (
                <>
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              ) : (
                'Finish Training'
              )}
            </Button>
          )}
        </div>
      </main>

      {/* AI Chat Button - passes current question for context */}
      <ChatButton currentQuestion={currentQuestion} isExamMode={false} />
    </div>
    </ContentProtection>
  );
};

export default TrainingMode;
